import { FC, Fragment, useMemo } from 'react'
import {
  Checkbox,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { currencyFormatter, getDisplayDate } from '../../../utils'
import { SiteRecentBill } from './types'
import {
  bulkInvoiceTableHeaderCheckboxSx,
  bulkInvoiceTableHeaderSx,
  bulkInvoiceTableSeparatorSx,
} from './bulkInvoiceTableStyles'

const TEXT_MAX_LEN = 30

const compactCellSx = { py: 1, px: 1 }

const noVerticalBorderSx = {
  borderLeft: 'none',
  borderRight: 'none',
}

const headerCellSx = {
  ...compactCellSx,
  ...noVerticalBorderSx,
  ...bulkInvoiceTableHeaderSx,
}

const bodyCellSx = {
  ...compactCellSx,
  ...noVerticalBorderSx,
}

const partyColumnSx = { maxWidth: 170 }

const siteColumnSx = { maxWidth: 170 }

function truncateText(value: string | null | undefined, maxLength = TEXT_MAX_LEN): string {
  const text = value ?? ''
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength)}…`
}

function renderTruncatedCell(value: string) {
  const display = truncateText(value)
  if (display === value) {
    return display
  }
  return (
    <Tooltip title={value} arrow placement="top">
      <Typography
        component="span"
        variant="body2"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  )
}

function normalizeSiteList(
  sites: SiteRecentBill[] | null | undefined
): SiteRecentBill[] {
  if (!sites) return []
  const list = Array.isArray(sites) ? sites : Object.values(sites)
  return list.filter(
    (site): site is SiteRecentBill =>
      site != null && typeof site === 'object' && site.billId != null
  )
}

function toBillId(id: number | string): number {
  return Number(id)
}

function getSelectAllSiteIds(siteList: SiteRecentBill[]): number[] {
  return siteList
    .map((site) => toBillId(site.billId))
    .filter((id) => Number.isFinite(id))
}

function isBillIdSelected(selectedIds: number[], billId: number | string): boolean {
  const id = toBillId(billId)
  return Number.isFinite(id) && selectedIds.some((selectedId) => selectedId === id)
}

function formatInvoicePeriod(site: SiteRecentBill): string {
  if (site.unbilledDays === -1) {
    return '-'
  }
  const start = site.startDate ?? site.invoiceDate
  const end = site.endDate
  if (start && end) {
    return `${getDisplayDate(start)} – ${getDisplayDate(end)}`
  }
  if (end) {
    return getDisplayDate(end)
  }
  if (start) {
    return getDisplayDate(start)
  }
  return '-'
}

export interface SelectSitesStepProps {
  sites: SiteRecentBill[] | null
  selectedSiteIds: number[]
  onSelectedSiteIdsChange: (ids: number[]) => void
}

export const SelectSitesStep: FC<SelectSitesStepProps> = ({
  sites,
  selectedSiteIds,
  onSelectedSiteIdsChange,
}) => {
  const siteList = useMemo(() => normalizeSiteList(sites), [sites])

  const isAllSelected =
    siteList.length > 0 &&
    siteList.every((site) => isBillIdSelected(selectedSiteIds, site.billId))
  const isIndeterminate =
    siteList.some((site) => isBillIdSelected(selectedSiteIds, site.billId)) &&
    !isAllSelected

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectedSiteIdsChange([])
      return
    }
    onSelectedSiteIdsChange(getSelectAllSiteIds(siteList))
  }

  const handleSelectSite = (billId: number | string) => {
    const id = toBillId(billId)
    if (!Number.isFinite(id)) return

    if (selectedSiteIds.includes(id)) {
      onSelectedSiteIdsChange(selectedSiteIds.filter((selectedId) => selectedId !== id))
    } else {
      onSelectedSiteIdsChange([...selectedSiteIds, id])
    }
  }

  let count = 0

  return (
    <>
      <Divider sx={bulkInvoiceTableSeparatorSx} />
      <TableContainer sx={{ maxHeight: 'calc(100vh - 280px)', overflow: 'auto' }}>
        <Table aria-label="recent bills" size="small" stickyHeader>
          <TableHead
            sx={{
              height: 44,
              '& .MuiTableCell-root': headerCellSx,
            }}
          >
            <TableRow>
              <TableCell padding="checkbox" width={50} sx={headerCellSx}>
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'select all sites' }}
                  sx={bulkInvoiceTableHeaderCheckboxSx}
                />
              </TableCell>
              <TableCell sx={{ ...headerCellSx, width: 40 }}>S.N.</TableCell>
              <TableCell sx={{ ...headerCellSx, ...partyColumnSx }}>Party Name</TableCell>
              <TableCell sx={{ ...headerCellSx, ...siteColumnSx }}>Site</TableCell>
              <TableCell sx={{ ...headerCellSx, whiteSpace: 'nowrap' }}>From – To</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }}>Created On</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 100 }}>Amount</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 90 }}>Unbilled</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {siteList.map((site, index) => {
                const hasInvoice = Number(site.unbilledDays) !== -1
                const siteAddress = site.siteAddress || ''

                return (
                  <Fragment key={`${site.billId}-${site.interimBillId ?? 0}-${index}`}>
                    <TableRow sx={{ '& td': bodyCellSx }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isBillIdSelected(selectedSiteIds, site.billId)}
                          onChange={() => handleSelectSite(site.billId)}
                          inputProps={{ 'aria-label': `select site ${site.billId}` }}
                          sx={{ py: 0 }}
                        />
                      </TableCell>
                      <TableCell>{++count}</TableCell>
                      <TableCell sx={partyColumnSx}>
                        {renderTruncatedCell(site.customerName)}
                      </TableCell>
                      <TableCell sx={siteColumnSx}>
                        <Tooltip
                          arrow
                          title={siteAddress ? siteAddress : 'Go to account details'}
                        >
                          <Link
                            underline="always"
                            variant="body2"
                            href={`/slips/view-slips/${site.billId}/${site.customerId}?tab_index=6`}
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {truncateText(siteAddress)}
                          </Link>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {formatInvoicePeriod(site)}
                      </TableCell>
                      {hasInvoice ? (
                        <>
                          <TableCell>
                            {getDisplayDate(site.actualCreateDate)}
                          </TableCell>
                          <TableCell>
                            {currencyFormatter.format(site.invoiceTotal)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: site.bgColor }}>
                              {site.unbilledDays} days
                            </Typography>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>-</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>-</TableCell>
                        </>
                      )}
                    </TableRow>
                  </Fragment>
                )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default SelectSitesStep
